"use client";

import { useMemo, useState } from "react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";

type AutonomyMode =
  | "observe"
  | "draft"
  | "ask_before_acting"
  | "auto_execute_low_risk"
  | "emergency_lock";

type PermissionLevel = "ask_for_permission" | "auto_mode" | "bypass_permissions";

interface PermissionPolicyRow {
  _id: string;
  tenantId: string;
  actionType: string;
  audience: string;
  currentMode: AutonomyMode;
  defaultMode: AutonomyMode;
  permissionLevel?: PermissionLevel;
  defaultPermissionLevel?: PermissionLevel;
  bypassExpiresAt?: number;
  bypassReason?: string;
  advancementProposedAt?: number;
  advancementProposedToMode?: AutonomyMode;
  advancementProposedToPermissionLevel?: PermissionLevel;
  customerOverride?: {
    customerId: string;
    grantedMode: AutonomyMode;
    permissionLevel?: PermissionLevel;
    grantedAt: number;
  };
}

const listPolicies = makeFunctionReference<"query", { tenantId: string }, PermissionPolicyRow[]>(
  "permissionPolicies:listByTenant",
);
const updateMode = makeFunctionReference<
  "mutation",
  { tenantId: string; policyId: string; currentMode: AutonomyMode; actorId: string },
  { policyId: string; currentMode: AutonomyMode }
>("permissionPolicies:updateMode");
const updatePermissionLevel = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    policyId: string;
    permissionLevel: PermissionLevel;
    bypassExpiresAt?: number;
    bypassDurationHours?: number;
    bypassReason?: string;
    actorId: string;
  },
  { policyId: string; permissionLevel: PermissionLevel; currentMode: AutonomyMode }
>("permissionPolicies:updatePermissionLevel");
const approveAdvancement = makeFunctionReference<
  "mutation",
  { tenantId: string; policyId: string; actorId: string },
  { policyId: string; currentMode: AutonomyMode }
>("permissionPolicies:approveAdvancement");
const setCustomerOverride = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    policyId: string;
    customerId?: string;
    grantedMode?: AutonomyMode;
    permissionLevel?: PermissionLevel;
    actorId: string;
  },
  { policyId: string }
>("permissionPolicies:setCustomerOverride");
const emergencyLockAll = makeFunctionReference<
  "mutation",
  { tenantId: string; actorId: string },
  { locked: number }
>("permissionPolicies:emergencyLockAll");

const modes: AutonomyMode[] = [
  "observe",
  "draft",
  "ask_before_acting",
  "auto_execute_low_risk",
  "emergency_lock",
];

const permissionLevels: PermissionLevel[] = ["ask_for_permission", "auto_mode", "bypass_permissions"];

export function AutonomySettings({ tenantId }: { tenantId: string }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <AutonomySettingsPanel tenantId={tenantId} policies={demoPolicies(tenantId)} />;
  }
  return <LiveAutonomySettings tenantId={tenantId} />;
}

function LiveAutonomySettings({ tenantId }: { tenantId: string }) {
  const policies = useQuery(listPolicies, { tenantId });
  if (policies === undefined) {
    return (
      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_380px] gap-4 px-6 py-5">
        <QuerySkeleton surface="autonomySettings" />
        <QuerySkeleton surface="agentTimeline" />
      </div>
    );
  }
  return <AutonomySettingsPanel tenantId={tenantId} policies={policies} />;
}

function AutonomySettingsPanel({
  tenantId,
  policies,
}: {
  tenantId: string;
  policies: PermissionPolicyRow[];
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(policies[0]?._id);
  const [customerId, setCustomerId] = useState("");
  const [bypassHours, setBypassHours] = useState("8");
  const [bypassReason, setBypassReason] = useState("Delegated operations window");
  const updateModeMutation = useMutation(updateMode);
  const updatePermissionLevelMutation = useMutation(updatePermissionLevel);
  const approveAdvancementMutation = useMutation(approveAdvancement);
  const setCustomerOverrideMutation = useMutation(setCustomerOverride);
  const emergencyLockAllMutation = useMutation(emergencyLockAll);

  const filtered = useMemo(
    () =>
      policies.filter((policy) =>
        `${policy.actionType} ${policy.audience}`.toLowerCase().includes(search.toLowerCase()),
      ),
    [policies, search],
  );
  const selected = policies.find((policy) => policy._id === selectedId) ?? filtered[0];
  const proposals = policies.filter((policy) => policy.advancementProposedAt);

  async function runModeUpdate(policyId: string, currentMode: AutonomyMode) {
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) return;
    await updateModeMutation({ tenantId, policyId, currentMode, actorId: "current-user" });
  }

  async function runPermissionLevelUpdate(policyId: string, level: PermissionLevel) {
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) return;
    const hours = Number(bypassHours);
    await updatePermissionLevelMutation({
      tenantId,
      policyId,
      permissionLevel: level,
      bypassDurationHours: level === "bypass_permissions" && Number.isFinite(hours) ? hours : undefined,
      bypassReason: level === "bypass_permissions" ? bypassReason : undefined,
      actorId: "current-user",
    });
  }

  async function runApprove(policyId: string) {
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) return;
    await approveAdvancementMutation({ tenantId, policyId, actorId: "current-user" });
  }

  async function runCustomerOverride(mode?: AutonomyMode) {
    if (!selected || !process.env.NEXT_PUBLIC_CONVEX_URL) return;
    await setCustomerOverrideMutation({
      tenantId,
      policyId: selected._id,
      customerId: mode ? customerId : undefined,
      grantedMode: mode,
      actorId: "current-user",
    });
  }

  async function runCustomerPermissionOverride(level?: PermissionLevel) {
    if (!selected || !process.env.NEXT_PUBLIC_CONVEX_URL) return;
    await setCustomerOverrideMutation({
      tenantId,
      policyId: selected._id,
      customerId: level ? customerId : undefined,
      permissionLevel: level,
      actorId: "current-user",
    });
  }

  async function runEmergencyLock() {
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) return;
    if (!window.confirm("Emergency-lock every permission policy for this tenant?")) return;
    await emergencyLockAllMutation({ tenantId, actorId: "current-user" });
  }

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_380px] gap-4 px-6 py-5">
      <section className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Autonomy Modes</h1>
            <p className="text-sm text-muted-foreground">Tenant policy by action and audience.</p>
          </div>
          <Button variant="secondary" onClick={runEmergencyLock}>
            Lock everything
          </Button>
        </div>

        {proposals.length > 0 ? (
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-sm">Advancement Proposals</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-3">
              {proposals.map((policy) => (
                <div key={policy._id} className="flex items-center justify-between gap-3 text-sm">
                  <span>
                    {policy.actionType} / {policy.audience} to {policy.advancementProposedToMode}
                  </span>
                  <Button className="h-8" onClick={() => runApprove(policy._id)}>
                    Approve
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filter by action or audience"
          className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
        />

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Audience</th>
                  <th className="px-3 py-2">Permission</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Default</th>
                  <th className="px-3 py-2">Override</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((policy) => (
                  <tr
                    key={policy._id}
                    className="cursor-pointer border-b border-border hover:bg-muted"
                    onClick={() => setSelectedId(policy._id)}
                  >
                    <td className="px-3 py-2 font-medium">{policy.actionType}</td>
                    <td className="px-3 py-2">{policy.audience}</td>
                    <td className="px-3 py-2">
                      <Badge variant={levelVariant(effectiveLevel(policy))}>{levelLabel(effectiveLevel(policy))}</Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{policy.currentMode}</td>
                    <td className="px-3 py-2 text-muted-foreground">{policy.defaultMode}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {policy.customerOverride?.customerId
                        ? `${policy.customerOverride.customerId} / ${levelLabel(
                            policy.customerOverride.permissionLevel ?? levelForMode(policy.customerOverride.grantedMode),
                          )}`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      <aside>
        <Card className="sticky top-4">
          <CardHeader className="p-3">
            <CardTitle className="text-sm">{selected?.actionType ?? "Select policy"}</CardTitle>
          </CardHeader>
          {selected ? (
            <CardContent className="space-y-4 p-3 text-sm">
              <div>
                <div className="text-xs uppercase text-muted-foreground">Permission Level</div>
                <div className="mt-2 grid gap-2">
                  {permissionLevels.map((level) => (
                    <Button
                      key={level}
                      variant={level === effectiveLevel(selected) ? "primary" : "secondary"}
                      className="h-8 justify-start"
                      onClick={() => runPermissionLevelUpdate(selected._id, level)}
                    >
                      {levelLabel(level)}
                    </Button>
                  ))}
                </div>
              </div>

              {effectiveLevel(selected) === "bypass_permissions" ? (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <div className="font-medium">Bypass is active</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Business approval gates are bypassed for this action. Platform security and hard blocks still apply.
                  </div>
                  {selected.bypassExpiresAt ? (
                    <div className="mt-2 text-xs">Expires {new Date(selected.bypassExpiresAt).toLocaleString()}</div>
                  ) : null}
                  {selected.bypassReason ? <div className="mt-1 text-xs">{selected.bypassReason}</div> : null}
                </div>
              ) : null}

              <div>
                <div className="text-xs uppercase text-muted-foreground">Bypass settings</div>
                <input
                  value={bypassHours}
                  onChange={(event) => setBypassHours(event.target.value)}
                  placeholder="hours"
                  className="mt-2 h-8 w-full rounded-md border border-border bg-card px-2 text-sm"
                />
                <input
                  value={bypassReason}
                  onChange={(event) => setBypassReason(event.target.value)}
                  placeholder="reason"
                  className="mt-2 h-8 w-full rounded-md border border-border bg-card px-2 text-sm"
                />
              </div>

              <div>
                <div className="text-xs uppercase text-muted-foreground">Legacy autonomy mode</div>
                <div className="mt-2 grid gap-2">
                  {modes.map((mode) => (
                    <Button
                      key={mode}
                      variant={mode === selected.currentMode ? "primary" : "secondary"}
                      className="h-8 justify-start"
                      onClick={() => runModeUpdate(selected._id, mode)}
                    >
                      {mode}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="text-xs uppercase text-muted-foreground">Per-customer override</div>
                <input
                  value={customerId}
                  onChange={(event) => setCustomerId(event.target.value)}
                  placeholder="customer id"
                  className="mt-2 h-8 w-full rounded-md border border-border bg-card px-2 text-sm"
                />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Button
                    className="h-8"
                    variant="secondary"
                    onClick={() => runCustomerPermissionOverride("ask_for_permission")}
                  >
                    Ask
                  </Button>
                  <Button
                    className="h-8"
                    variant="secondary"
                    onClick={() => runCustomerPermissionOverride("auto_mode")}
                  >
                    Auto
                  </Button>
                  <Button
                    className="h-8"
                    variant="secondary"
                    onClick={() => runCustomerPermissionOverride("bypass_permissions")}
                  >
                    Bypass
                  </Button>
                  <Button className="h-8" variant="secondary" onClick={() => runCustomerOverride(undefined)}>
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-3 text-sm text-muted-foreground">No policies found.</CardContent>
          )}
        </Card>
      </aside>
    </div>
  );
}

function levelVariant(level: PermissionLevel): "neutral" | "good" | "warn" | "bad" | "info" {
  if (level === "bypass_permissions") return "bad";
  if (level === "auto_mode") return "good";
  return "warn";
}

function levelLabel(level: PermissionLevel) {
  if (level === "ask_for_permission") return "Ask For Permission";
  if (level === "auto_mode") return "Auto Mode";
  return "Bypass Permissions";
}

function levelForMode(mode: AutonomyMode): PermissionLevel {
  return mode === "auto_execute_low_risk" ? "auto_mode" : "ask_for_permission";
}

function effectiveLevel(policy: PermissionPolicyRow): PermissionLevel {
  return policy.permissionLevel ?? levelForMode(policy.currentMode);
}

function demoPolicies(tenantId: string): PermissionPolicyRow[] {
  return [
    {
      _id: "policy_1",
      tenantId,
      actionType: "request_eta",
      audience: "carrier",
      currentMode: "auto_execute_low_risk",
      defaultMode: "ask_before_acting",
      permissionLevel: "auto_mode",
      defaultPermissionLevel: "ask_for_permission",
    },
    {
      _id: "policy_2",
      tenantId,
      actionType: "create_load",
      audience: "internal",
      currentMode: "ask_before_acting",
      defaultMode: "ask_before_acting",
      permissionLevel: "ask_for_permission",
      defaultPermissionLevel: "ask_for_permission",
      advancementProposedAt: Date.now() - 3_600_000,
      advancementProposedToMode: "ask_before_acting",
    },
    {
      _id: "policy_3",
      tenantId,
      actionType: "send_customer_quote",
      audience: "customer",
      currentMode: "auto_execute_low_risk",
      defaultMode: "ask_before_acting",
      permissionLevel: "bypass_permissions",
      defaultPermissionLevel: "ask_for_permission",
      bypassExpiresAt: Date.now() + 8 * 3_600_000,
      bypassReason: "Demo delegated quote handling",
    },
  ];
}
